import React from 'react';
import TenantCompanyMenus from '../saas/TenantCompanyMenus';
import CompanyModuleRuntimeGroups, {
  groupRuntimeIssues,
  statusPillStyle,
} from './CompanyModuleRuntimeGroups';
import CompanyModuleScopePolicyPanel from './CompanyModuleScopePolicyPanel';
import {
  chipStyle,
  chipWrapStyle,
  panelSubStyle,
  panelTitleStyle,
  rowMetaStyle,
  rowTitleStyle,
  summaryCardStyle,
  summaryGridStyle,
  summaryLabelStyle,
  summaryValueStyle,
} from './dashboardStyles';

function SummaryCard({ label, value, help }) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>{label}</div>
      <div style={summaryValueStyle}>{value ?? '-'}</div>
      {help ? <div style={{ ...rowMetaStyle, marginTop: 4 }}>{help}</div> : null}
    </div>
  );
}

function CompanyCard({ company }) {
  const mismatchCount = Number(company.mismatchCount || 0);
  const missingCount = Number(company.missingCount || 0);
  const runtimeDangerCount = Number(company.runtimeDangerCount || 0);
  const runtimeWarningCount = Number(company.runtimeWarningCount || 0);
  const issueCount = mismatchCount + missingCount + runtimeDangerCount + runtimeWarningCount;
  const status = runtimeDangerCount > 0 || mismatchCount + missingCount > 0
    ? 'danger'
    : runtimeWarningCount > 0
      ? 'warning'
      : 'ok';

  return (
    <div style={{ ...summaryCardStyle, minHeight: 120 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div>
          <div style={rowTitleStyle}>{company.companyName || company.companyCode}</div>
          <div style={rowMetaStyle}>{company.companyCode}</div>
        </div>
        <span style={statusPillStyle(status)}>
          {status === 'ok' ? '정상' : status === 'warning' ? '확인' : '위험'}
        </span>
      </div>
      <div style={chipWrapStyle}>
        <span style={chipStyle}>회사 ON {company.enabledModuleCount}/{company.totalModuleCount}</span>
        <span style={chipStyle}>기존 ON {company.legacyEnabledModuleCount}/{company.totalModuleCount}</span>
        <span style={chipStyle}>권한 위험 {runtimeDangerCount}</span>
        <span style={chipStyle}>권한 확인 {runtimeWarningCount}</span>
      </div>
      <div style={{ ...rowMetaStyle, marginTop: 8 }}>
        {status === 'ok'
          ? '회사 기능 원장과 실제 권한 상태가 충돌하지 않습니다.'
          : `점검 필요 ${issueCount}건 · 원장 불일치 ${mismatchCount}건 · 누락 ${missingCount}건`}
      </div>
    </div>
  );
}

function LegacyMismatchList({ mismatches }) {
  if (!mismatches.length) {
    return (
      <div style={{ ...summaryCardStyle, borderColor: 'rgba(22,163,74,0.35)', background: 'rgba(22,163,74,0.08)' }}>
        <div style={rowTitleStyle}>기존 tenant 원장과 회사 원장이 모두 일치합니다.</div>
        <div style={rowMetaStyle}>아직 실제 메뉴 권한에는 연결하지 않은 읽기 전용 비교입니다.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {mismatches.slice(0, 12).map((item) => (
        <div
          key={`${item.companyCode}:${item.moduleCode}`}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gap: 8,
            border: '1px solid rgba(220,38,38,0.35)',
            borderRadius: 7,
            padding: '7px 8px',
            background: 'rgba(220,38,38,0.08)',
          }}
        >
          <div>
            <div style={rowTitleStyle}>{item.companyName || item.companyCode} / {item.moduleName || item.moduleCode}</div>
            <div style={rowMetaStyle}>{item.status}</div>
          </div>
          <span style={statusPillStyle('danger')}>
            회사 {item.companyEnabled ? 'ON' : 'OFF'} · 기존 {item.legacyEnabled ? 'ON' : 'OFF'}
          </span>
        </div>
      ))}
      {mismatches.length > 12 ? <div style={rowMetaStyle}>외 {mismatches.length - 12}건이 더 있습니다.</div> : null}
    </div>
  );
}

// 회사별 메뉴를 정하는 부품은 하나(saas/CompanyMenuPanel — 2026-10-19 사용자 규칙). 운영자는 테넌트 관리 › 회사 탭에서 정한다.
//   여기(운영자 점검 화면)에 있던 '회사별 기능 제공 설정' 토글과 '회사 원장 끄기' 버튼은 없애고 안내만 남긴다(읽기 전용 점검).
//   테넌트 관리자는 테넌트 관리 화면에 못 들어가므로 같은 부품을 여기서 쓴다(TenantCompanyMenus).
function CompanyMenuMovedNotice() {
  return (
    <div style={{ ...summaryCardStyle, padding: 12 }}>
      <div style={rowTitleStyle}>회사별 메뉴는 테넌트 관리에서 정합니다</div>
      <div style={rowMetaStyle}>테넌트 관리 › 회사 탭에서 회사를 펼치면 &apos;메뉴&apos;가 있습니다(테넌트가 받은 메뉴 안에서만).</div>
    </div>
  );
}

// budgetScoped: 테넌트 관리자 화면 — 테넌트 관리와 같은 회사 메뉴 부품(자기 테넌트만).
export default function CompanyModuleShadowPanel({ report, onRefresh, budgetScoped = false }) {
  if (!report) return null;
  if (budgetScoped) return <TenantCompanyMenus report={report} />;
  const summary = report.summary || {};
  const companies = summary.companies || [];
  const mismatches = report.mismatches || [];
  const runtimeIssues = report.runtimeIssues || [];
  const groups = groupRuntimeIssues(runtimeIssues);
  const mismatchCount = Number(summary.mismatchCount || mismatches.length || 0);
  const runtimeDangerCount = Number(summary.runtimeDangerCount || 0);
  const nonActionTypes = ['operator_keep', 'scope_excluded'];
  const actionNeededCount = runtimeIssues.filter((item) => !nonActionTypes.includes(item.triage?.type)).length;
  const keepCount = runtimeIssues.filter((item) => item.triage?.type === 'operator_keep').length;
  const overallStatus = mismatchCount > 0 || runtimeDangerCount > 0
    ? 'danger'
    : actionNeededCount > 0
      ? 'warning'
      : 'ok';

  return (
    <section style={{ ...summaryCardStyle, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h3 style={panelTitleStyle}>PINEGROUP 회사별 기능 점검</h3>
          <div style={panelSubStyle}>
            tenant/package 기능과 회사별 기능, 실제 직원 권한을 분리하기 위한 읽기 전용 점검입니다.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={statusPillStyle(report.ready ? overallStatus : 'issue')}>
            {report.ready && overallStatus === 'ok' ? '안전' : overallStatus === 'warning' ? '정리 필요' : '위험 확인'}
          </span>
          {onRefresh ? (
            <button
              type="button"
              onClick={onRefresh}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 7,
                background: 'var(--bg-card)',
                color: 'var(--text-sub)',
                padding: '5px 8px',
                fontSize: 12,
                fontWeight: 850,
                cursor: 'pointer',
              }}
            >
              새로고침
            </button>
          ) : null}
        </div>
      </div>

      {!report.ready ? (
        <div style={{ ...summaryCardStyle, marginTop: 10 }}>
          <div style={rowTitleStyle}>company module shadow 테이블이 아직 준비되지 않았습니다.</div>
          <div style={rowMetaStyle}>{report.message || '마이그레이션 상태를 확인하세요.'}</div>
        </div>
      ) : (
        <>
          <div style={{ ...summaryGridStyle, marginTop: 10 }}>
            <SummaryCard label="기준 tenant" value={report.tenantCode || 'PINEGROUP'} help="shadow 기준" />
            <SummaryCard label="회사" value={summary.companyCount} help="PINE/DOT/LNJ" />
            <SummaryCard label="점검 행" value={summary.totalRowCount} help="회사별 전체 모듈" />
            <SummaryCard label="원장 불일치" value={mismatchCount} help="기존 tenant 기준과 회사 기준 차이" />
            <SummaryCard label="조치 필요" value={actionNeededCount} help={groups.filter((group) => !nonActionTypes.includes(group.type)).map((group) => `${group.label} ${group.items.length}`).join(' · ') || '없음'} />
            <SummaryCard label="미배정 정상" value={keepCount} help="PINE 운영자 전용 후보" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginTop: 10 }}>
            {companies.map((company) => (
              <CompanyCard key={company.companyCode} company={company} />
            ))}
          </div>

          <div style={{ marginTop: 10 }}>
            <CompanyMenuMovedNotice />
          </div>

          <div style={{ marginTop: 10 }}>
            <CompanyModuleScopePolicyPanel report={report.scopePolicyReport} />
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={{ ...rowTitleStyle, marginBottom: 6 }}>실제 직원 권한 점검</div>
            <CompanyModuleRuntimeGroups
              issues={runtimeIssues}
              savingKey=""
            />
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={{ ...rowTitleStyle, marginBottom: 6 }}>기존 tenant 원장과 회사 원장 비교</div>
            <LegacyMismatchList mismatches={mismatches} />
          </div>
        </>
      )}
    </section>
  );
}
